import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, RefreshCw, Trash2, User, BookOpen, Compass } from "lucide-react";
import { API, lifeChatApi } from "@/lib/api";
import { useLifeState } from "@/context/LifeStateContext";
import { computeLifeMetrics, iso, verseFor } from "@/lib/life";
import { PageHeader, SectionCard } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const authHeader = () => {
  const token = localStorage.getItem("wcc_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SUGGESTIONS = [
  "What should I focus on today given my energy?",
  "Help me plan a restful but productive week.",
  "Which life area needs my attention most?",
  "Encourage me with Scripture for this season.",
];

const Dot = ({ d = 0 }) => <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--text-faint)] animate-bounce" style={{ animationDelay: `${d}s` }} />;

const Bubble = ({ role, content, streaming }) => {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
      <span className={`grid place-items-center h-8 w-8 rounded-full shrink-0 ${isUser ? "bg-[color:rgba(23,35,31,0.08)] text-[var(--text-muted)]" : "bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)]"}`}>
        {isUser ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </span>
      <div className={`max-w-[80%] rounded-[14px] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-[var(--green)] text-[#f7fbf9]" : "bg-white border border-[color:var(--border-subtle)] text-[var(--text)]"}`}>
        {content || (streaming ? <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span> : "")}
      </div>
    </div>
  );
};

export default function CommandAI() {
  const { state, loading, wealthStewardship } = useLifeState();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [histLoading, setHistLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    lifeChatApi.history().then((d) => setMessages(d.messages || [])).catch(() => {}).finally(() => setHistLoading(false));
  }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/life/chat`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeader() }, body: JSON.stringify({ message: msg }) });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta }; return copy; });
          } else if (payload.error) { toast.error("Command AI error: " + payload.error); }
        }
      }
    } catch (e) {
      toast.error("Could not reach Command AI");
      setMessages((m) => { const copy = [...m]; if (copy.length && copy[copy.length - 1].role === "assistant" && !copy[copy.length - 1].content) copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." }; return copy; });
    } finally { setStreaming(false); }
  };

  const clearChat = async () => { try { await lifeChatApi.clear(); setMessages([]); toast.success("Chat cleared"); } catch { toast.error("Could not clear chat"); } };

  const m = state ? computeLifeMetrics(state, iso(), wealthStewardship ? { "Wealth & Stewardship": wealthStewardship.score } : {}) : null;
  const verse = verseFor(iso());

  return (
    <div>
      <PageHeader title="Command AI" subtitle="Grounded, Scripture-informed guidance that knows your season and your data." testid="command-ai-header" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <SectionCard title="Chat with Command AI" right={messages.length ? <Button variant="ghost" size="sm" onClick={clearChat} className="text-[var(--text-muted)]" data-testid="life-clear-chat-button"><Trash2 className="h-3.5 w-3.5 mr-1" />Clear</Button> : null} testid="life-chat-card" contentClass="flex flex-col">
            <div ref={scrollRef} className="h-[54vh] overflow-auto space-y-4 pr-1" data-testid="life-chat-messages">
              {histLoading ? <Skeleton className="h-24 rounded-[12px]" /> : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)] mb-4"><Sparkles className="h-6 w-6" /></span>
                  <p className="font-serif-display text-lg text-[var(--text)]">Ask for grounded guidance</p>
                  <p className="text-sm text-[var(--text-muted)] mt-1 max-w-sm">I can see your season, focus, tasks and life areas. Try one of these:</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={i} onClick={() => send(s)} data-testid={`life-chat-suggestion-${i}`} className="text-sm rounded-full border border-[color:var(--border-subtle)] bg-white px-3 py-1.5 text-[var(--text)] hover:border-[color:rgba(31,115,85,0.4)] hover:bg-[color:rgba(31,115,85,0.06)] transition-colors">{s}</button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((mm, i) => <Bubble key={i} role={mm.role} content={mm.content} streaming={streaming && i === messages.length - 1 && mm.role === "assistant"} />)
              )}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 mt-4 pt-4 border-t border-[color:var(--border-subtle)]">
              <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={streaming} placeholder="Ask about your day, season, rest, or a hard decision…" className="bg-white border-[color:var(--border-subtle)]" data-testid="life-chat-input" />
              <Button type="submit" disabled={streaming || !input.trim()} className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]" data-testid="life-chat-send-button">{streaming ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
            </form>
          </SectionCard>
        </div>

        <div className="lg:col-span-4 space-y-4">
          <SectionCard title="Your context" testid="life-context-card">
            {loading || !m ? <Skeleton className="h-24" /> : (
              <div className="space-y-2.5 text-sm">
                <div className="flex items-center justify-between"><span className="text-[var(--text-muted)]">Season</span><span className="text-[var(--text)] font-medium">{state.season}</span></div>
                <div className="flex items-center justify-between"><span className="text-[var(--text-muted)]">Life score</span><span className="text-[var(--text)] font-medium tabular-nums">{m.lifeScore}</span></div>
                <div className="flex items-center justify-between"><span className="text-[var(--text-muted)]">Energy</span><span className="text-[var(--text)] font-medium tabular-nums">{state.energy}%</span></div>
                <div className="flex items-center justify-between"><span className="text-[var(--text-muted)]">Needs a lift</span><span className="text-[var(--text)] font-medium">{m.lowest.area.short}</span></div>
                <div className="pt-2 border-t border-[color:var(--border-subtle)] flex items-start gap-2 text-[12px] text-[var(--text-muted)]"><Compass className="h-4 w-4 text-[var(--green)] shrink-0 mt-0.5" />{state.focus}</div>
              </div>
            )}
          </SectionCard>
          <div className="rounded-[var(--radius-card)] p-5 text-[#f7fbf9]" style={{ background: "linear-gradient(135deg,#1f7355,#17231f)" }} data-testid="life-ai-verse">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#e7d9bf]"><BookOpen className="h-3.5 w-3.5" />Verse for today</div>
            <p className="font-serif-display text-lg leading-relaxed mt-3">“{verse.text}”</p>
            <p className="text-sm text-[#cfe3d8] mt-2">{verse.reference}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
